import React, { useCallback, useEffect, useState } from "react";
// -- Fabric UI

import { Badge, Button, Persona, makeStyles } from "@fluentui/react-components";
import {
  AlertRegular,
  GridDots28Filled,
  PersonFilled,
  Settings24Regular,
} from "@fluentui/react-icons";
import { Flex, StyledTooltip } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { devices } from "library/constants";
import { INotification, SelectItem } from "library/types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GrecoPanel } from "./GrecoPanel/GrecoPanel";
import { IGrecoPanelStrings } from "./GrecoPanel/IGrecoPanelStrings";
import { PanelType } from "./GrecoPanel/PanelType";
import { IGrecoHeaderStrings } from "./IGrecoHeaderStrings";
import { NavigationPanel } from "./NavigationPanel/NavigationPanel";
import { HeaderTick } from "./HeaderTick";

interface IProps {
  headerConfig?: any;
  user?: any;
  panelStatus?: (status: boolean) => void;
  logo?: any;
  logout?: () => void;
  darkMode?: any;
  headerStrings?: IGrecoHeaderStrings;
  notifications?: INotification[];
  navigation?: JSX.Element;
  clearNotifications?: () => void;
  languageOptions?: SelectItem[];
  defaultLanguage?: string;
  onChangeLanguage?: (
    event: React.FormEvent<HTMLDivElement>,
    option?: SelectItem,
    index?: number
  ) => void;
  isMobile?: boolean;
}

type statePanelType = PanelType | null;

const GrecoHeader = (props: IProps) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [panelType, setPanelType] = useState<statePanelType>(null);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [panelStrings, setPanelStrings] = useState<
    IGrecoPanelStrings | undefined
  >();
  const [headerConfig, setHeaderConfig] = useState({
    application_name: "GrECo Header",
    notifications_visible: true,
    help_visible: true,
    account: true,
    search_autofocus: false,
    logo_visible: true,
    title_visible: true,
    settings_visible: true,
    defaultLanguage: "en-GB",
    navigationPanelOpen: false,
    isMobile: true,
  });

  const onNavigationStateChange = useCallback((value) => {
    setHeaderConfig((prev) => ({
      ...prev,
      navigationPanelOpen: value,
    }));
  }, []);

  useEffect(() => {
    if (!props.headerConfig) return;
    setHeaderConfig((prev) => ({ ...prev, ...props.headerConfig }));
  }, [props.headerConfig]);

  useEffect(() => {
    if (!props.headerStrings || !panelType) return;

    if (panelType !== null) {
      const panelStr = updatePanelStrings(
        panelType,
        props.headerStrings
      ) as IGrecoPanelStrings;
      setPanelStrings(panelStr);
    }
  }, [panelType, props.headerStrings]);

  const updatePanelStrings = (
    panelType: PanelType,
    headerStrings
  ): IGrecoPanelStrings | undefined => {
    switch (panelType) {
      case PanelType.Notifications:
        return headerStrings.notificationsPanelStrings;
      case PanelType.Settings:
        return headerStrings.settingsPanelStrings;
      case PanelType.UserSettings:
        return headerStrings.userSettingsPanelStrings;
      default:
        return undefined;
    }
  };

  const togglePanel = (type) =>
    type === panelType ? closePanel() : openPanel(type);

  const openPanel = (type) => {
    if (props.panelStatus) props.panelStatus(true);
    setPanelIsOpen(true);
    setPanelType(type);
  };

  const closePanel = () => {
    if (props.panelStatus) props.panelStatus(false);
    setPanelIsOpen(false);
    setPanelType(null);
  };

  return (
    <Container>
      <GrecoPanel
        onDismiss={closePanel}
        panelIsOpen={panelIsOpen}
        panelType={panelType}
        userData={props.user}
        logout={props.logout as () => void}
        headerConfig={headerConfig}
        darkMode={props.darkMode}
        panelStrings={panelStrings}
        notifications={props.notifications}
        clearNotifications={props.clearNotifications}
        isMobile={headerConfig.isMobile}
        togglePanel={togglePanel as () => void}
        languageOptions={props.languageOptions}
        defaultSelectedLanguage={
          props.defaultLanguage || headerConfig.defaultLanguage
        }
        onChangeLanguage={props.onChangeLanguage}
      />
      {/* {props.navigation && (
        <>
          <StyledTooltip
            content={t("ila.navigation.label")}
            relationship="description"
          >
            <NavigationButton
              appearance="outline"
              onClick={() => {
                onNavigationStateChange(true);
              }}
              icon={<GridDots28Filled />}
            />
          </StyledTooltip>
          <HeaderTick />
        </>
      )} */}
      {/* <NavigationPanel
        navigation={props.navigation}
        onNavigationStateChange={onNavigationStateChange}
        navigationPanelOpen={headerConfig.navigationPanelOpen}
      /> */}

      <ContentWrap>
        <Flex>
          {headerConfig.logo_visible ? (
            <img
              className="logo"
              src={props.logo}
              alt={headerConfig.application_name}
              style={{
                height: "38px",
              }}
            />
          ) : null}
          {/* <img
            src={AppLogo}
            style={{
              marginLeft: "10px",
              width: "41px",
              height: "38px",
            }}
            alt="app_logo"
          /> */}
          {headerConfig.logo_visible ? (
            <AppName>{headerConfig.application_name}</AppName>
          ) : null}
        </Flex>
        <ActionIconsWrap>
          <Button
            icon={
              <div>
                <AlertRegular />
                <Badge
                  style={{
                    position: "absolute",
                    top: "5px",
                    left: "20px",
                    display: props.notifications?.length ? "block" : "none",
                  }}
                >
                  {props.notifications?.length}
                </Badge>
              </div>
            }
            onClick={() => togglePanel(PanelType.Notifications)}
            appearance="transparent"
            style={{
              color: theme.palette.white,
            }}
          />
          <Button
            icon={
              <div>
                <Settings24Regular />
              </div>
            }
            onClick={() => togglePanel(PanelType.Settings)}
            appearance="transparent"
            style={{
              color: theme.palette.white,
            }}
          />

          {headerConfig.account && props.user ? (
            <Persona
              onClick={() => togglePanel(PanelType.UserSettings)}
              {...{
                text: props.user?.displayName || "",
                imageUrl: props.user?.photoSmall || "",
              }}
              avatar={
                props.user && props.user?.displayName && props.user?.photoSmall
                  ? {
                      image: {
                        src: props.user?.photoSmall,
                      },
                    }
                  : {
                      icon: <PersonFilled />,
                    }
              }
              name=""
              secondaryText=""
              size="large"
            />
          ) : null}
        </ActionIconsWrap>
      </ContentWrap>
    </Container>
  );
};

export default GrecoHeader;

const Container = styled.div`
  background: ${({ theme }) => theme.palette.themePrimary};
  display: flex;
  width: 100%;
  // height: 50px;
  line-height: 50px;
`;

const ContentWrap = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  padding: 0 10px;
  @media only screen and ${devices.md} {
  }
`;
const AppName = styled.div`
  font-size: 16px;
  margin-left: 10px;
  color: ${({ theme }) => theme.palette.white};
  display: none;
  @media only screen and ${devices.md} {
    display: block;
  }
`;

const ActionIconsWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  @media only screen and ${devices.md} {
    gap: 20px;
  }
`;

const NavigationButton = styled(Button)`
  height: 50px;
  width: 50px;
  max-width: 50px;
  max-height: 50px;
  background: transparent;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.palette.white};
  &:hover {
    color: ${({ theme }) => theme.palette.white};
    transition-property: transform;
    transition-duration: 0.2s;
    transform: scale(1.1);
  }
`;
